import React from 'react';
import {useDispatch, useSelector} from "react-redux";
import { useAddPageMutation, useUpdatePageMutation} from "../../../../redux/apis/pagesApi";
import { Form, Formik} from "formik";
import s from "./PagesModal.module.scss";
import * as yup from 'yup';
import {Back} from "components/Back/Back";
import {Input} from "components/Input/Input";
import {SecondaryButton} from "components/SecondaryButton/SecondaryButton";
import {PrimaryButton} from "components/PrimaryButton/PrimaryButton";
import { ReactComponent as Check } from 'assets/icons/check.svg';
import {DropDown} from "components/DropDown/DropDown";
import {useStatusContext} from "components/StatusProvider";
import {useTranslation} from "react-i18next";
import {Preloader} from "components/Preloader/Preloader";

type PagesModal = {
    close: any;
    el: any;
    pageState: any;
    refetchGetPages: any;
}

export const PagesModal = ({ close, el, pageState, refetchGetPages }: PagesModal) => {
    const langs = useSelector((store: any) => store.main.langs);
    console.log("el", el)
    const { t } = useTranslation();
    const initialValues: any = {
        id: el? el.id : '',
        name: el? el.name : '',
        content: el? el.content : '',
        marker: el?.marker,
        head_scripts: el? el.head_scripts : '',
        body_scripts: el? el.body_scripts : ''
    };
    const [setPage, {isLoading: isLoadingSetPage}] = useAddPageMutation();
    const [updatePage, {isLoading: isLoadingUpdatePage}] = useUpdatePageMutation();
    const { openStatus } = useStatusContext();

    if (langs.isFetching || isLoadingSetPage || isLoadingUpdatePage) return <Preloader />

    langs.forEach((lang: any) => {
        initialValues[`title-${lang.key}`] = el? el.params?.head?.title[lang.key] : ''
        initialValues[`description-${lang.key}`] = el? el.params?.head?.description[lang.key] : ''
        initialValues[`keywords-${lang.key}`] = el? el.params?.head?.keywords[lang.key] : ''
    })

    const validation_data: any = {
        name: yup.string().required(t("validation.required")).min(2, t("validation.min2s")),
        marker: yup
            .string()
            .required(t("validation.required"))
            .test(
                "no-spaces",
                t("validation.noSpacesAllowed"),
                (value) => !/\s/.test(value)
            )
            .matches(
                /^[a-z0-9_]+$/,
                t("validation.invalidCharacters")
            )
            .min(2, t("validation.min2s"))
    }

    langs.forEach((el: any) => {
        validation_data[`title-${el.key}`] = yup.string().required(t("validation.required")).min(2, t("validation.min2s"))
        validation_data[`description-${el.key}`] = yup.string().required(t("validation.required")).min(2, t("validation.min2s"))
        validation_data[`keywords-${el.key}`] = yup.string().required(t("validation.required")).min(2, t("validation.min2s"))
    })

    const validationSchema = yup.object().shape(validation_data);

    return (
        <div className={s.pages_modal}>
            <Back close={close} text={t("pagesModal.backToPages")} />

            <div className={s.form}>
                <h2>{el? t("pagesModal.editPage") : t("pagesModal.addPage")}</h2>
                <Formik
                    validationSchema={validationSchema}
                    initialValues={initialValues}
                    onSubmit={async (values, {setSubmitting, resetForm})=> {
                        setSubmitting(false)
                        const head: any = {title: {}, description: {}, keywords: {}}
                        langs.forEach((el: any) => {
                            head.title[el.key] = values[`title-${el.key}`]
                            head.description[el.key] = values[`description-${el.key}`]
                            head.keywords[el.key] = values[`keywords-${el.key}`]
                        })
                        if(el){
                            const data = {id: initialValues.id, name: values.name, head: head, marker: values.marker, head_scripts: values.head_scripts, body_scripts: values.body_scripts}
                            const res: any = await updatePage(data);
                            if(res.error?.data.message === 1000) {
                                openStatus('warning', "status.unfortunately");
                                close(false);
                            }
                            else if(res.error?.data.message === 30) {
                                openStatus('error', "status.thereWasAnError");
                            }
                            else {
                                openStatus('success', "status.operationSuccessful");
                                refetchGetPages();
                                close(false);
                            }
                        }
                        else {
                            const data = {id: initialValues.id, name: values.name, head: head, marker: values.marker, head_scripts: values.head_scripts, body_scripts: values.body_scripts}
                            const res: any = await setPage(data);
                            if(res.error?.data.message === 1000){
                                openStatus('warning', "status.unfortunately");
                                close(false);
                            }
                            else if(res.error?.data.message === 30){
                                openStatus('error', `${t("pagesModal.name")} ${res.error.data.data.name} ${t("pagesModal.alreadyTaken")}`);
                            }
                            else {
                                openStatus('success', "status.operationSuccessful");
                                refetchGetPages();
                                close(false);
                            }
                        }
                    }}
                >
                    {({errors, touched, setFieldValue, values}) => (
                        <Form className={s.pages_form}>
                            <Input
                                value={values.name}
                                label={t("pagesModal.pageTitle")}
                                errors={errors.name}
                                touched={touched.name}
                                name={"name"}
                                onClick={() => setFieldValue('name', '')}
                            />

                            <Input
                                value={values.marker}
                                label={t("pages.marker")}
                                errors={errors.marker}
                                touched={touched.marker}
                                name={"marker"}
                                onClick={() => setFieldValue('marker', '')}
                            />

                            <DropDown
                                values={values}
                                langs={langs}
                                errors={errors}
                                touched={touched}
                                kind={"title"}
                                label={t("pagesModal.title")}
                            />

                            <DropDown
                                values={values}
                                langs={langs}
                                errors={errors}
                                touched={touched}
                                kind={"description"}
                                label={t("pagesModal.description")}
                            />

                            <DropDown
                                values={values}
                                langs={langs}
                                errors={errors}
                                touched={touched}
                                kind={"keywords"}
                                label={t("pagesModal.keywords")}
                            />

                            <div className={s.textarea_wrapper}>
                                <p className={s.title}>{t("pagesModal.head_scripts")}</p>
                                <textarea 
                                    name="head_scripts"
                                    value={values.head_scripts}
                                    onChange={(e) => setFieldValue('head_scripts', e.target.value)}
                                />
                            </div>

                            <div className={s.textarea_wrapper}>
                                <p className={s.title}>{t("pagesModal.body_scripts")}</p>
                                <textarea 
                                    name="body_scripts"
                                    value={values.body_scripts}
                                    onChange={(e) => setFieldValue('body_scripts', e.target.value)}
                                />
                            </div>

                            <div className={s.wrap_buttons}>
                                <PrimaryButton
                                    text={t("general.confirm")}
                                    type={"submit"}
                                    icon={<Check />}
                                />
                                <SecondaryButton
                                    text={t("general.cancel")}
                                    type={"button"}
                                    white
                                    onClick={() => close(false)}
                                />
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
        </div>
    )
}